import I18n from 'i18n-js';
import React from 'react';
import classNames from 'classnames';
import DynamicPageHead from './dynamic_page_head';
import Jumplinks, { IJumplink } from './jumplinks';
import NotFound from './not_found';
import ScrollTarget from './scroll_target';
import bind from '../bind';
import gql from 'graphql-tag';
import { JUMPLINK_LOCATION, LISTINGS_COLLECTION } from '../cms/component_types';
import { COMPONENT_OUTPUT } from '../cms/component_output';
import { cmsEditRoute, cmsPagePath } from '../url_helpers';
import { get } from 'lodash';
import { ChildProps, graphql } from 'react-apollo';
import { withUserContext, IUserContext } from './user_context_provider';
import { MParticlePageName, trackPageView } from '../elog/mparticle_tracker';

import experiments from '../experiments';
import { CommonsCmsDynamicPage } from '../rql';
import { RouteComponentProps } from 'react-router';
import { Redirect } from '../routing_result';
import CmsPageMetadata from './cms_page_metadata';
import trackCmsExperimentComponentFilter from '../track_cms_experiment_component_filter';
import { RCAlertBox } from '../rc/RCAlertBox';
import CmsPageComponent from './cms_page_component';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { pageShouldShowEmailPopUp, addOnExitTriggerForEmailPopUp } from '../email_pop_up_helpers';
import { trackLocalizeListingsRowExperiment } from '../seller_location_experiments';

const COMPONENTS_TO_RENDER_FIRST = [];

interface RouteParams {
  slug?: string;
  childSlug?: string;
  splat?: string;
}

interface ComponentProps {
  cmsPageType: string;
  slugOverride?: string;
}

interface IOwnProps extends RouteComponentProps<{}, RouteParams, ComponentProps> {
  componentOutputOverrides?: any;
  notFoundOverride?: React.ComponentClass;
}

type IProps = ChildProps<IUserContext & IOwnProps, CommonsCmsDynamicPage.Query>;

const MAIN_SECTION = 'main';
const SIDEBAR_SECTION = 'sidebar';

function DraftPageBanner() {
  return (
    <RCAlertBox type="info" small>
      <p className="align-center mb-0 weight-bold">
        Draft page
      </p>
    </RCAlertBox>
  );
}

export class DynamicPage extends React.Component<IProps, null> {
  scrollTarget = null;

  componentDidMount() {
    if (!this.page || this.page?.pageNotFound) return;

    if (this.scrollTarget) {
      const typedScrollTarget = this.scrollTarget as ScrollTarget;
      typedScrollTarget.scrollToTop();
    }

    this.trackPage();

    this.addEmailPopUpTriggerIfAllowedPage();
  }

  componentDidUpdate(oldProps) {
    if (this.scrollTarget) {
      const typedScrollTarget = this.scrollTarget as ScrollTarget;
      typedScrollTarget.scrollToTop();
    }

    if (!this.page?.pageNotFound && this.page?.slug !== oldProps.data?.page?.slug) {
      this.trackPage();
    }
  }

  get page() {
    return this.props.data?.pages?.[0];
  }

  get cmsListingsRowsCount() {
    const components = this.page?.components || [];
    return components.filter(component => (
      component.componentType === LISTINGS_COLLECTION
    )).length;
  }

  trackPage() {
    trackPageView({
      pageName: MParticlePageName.CMS,
      cmsPageSlug: this.page.slug,
      cmsPageType: this.page.pageType,
      experiments: formatExperimentsForEventAttributes(this.props.user, [
        experiments.DEALS_AND_STEALS_ROWS_V2,
        ...trackLocalizeListingsRowExperiment(this.props.user),
      ]),
      cmsListingsRowsCount: this.cmsListingsRowsCount,
    });
  }

  @bind
  createScrollTargetRef(element) {
    this.scrollTarget = element;
  }

  getData(componentResponse) {
    return JSON.parse(componentResponse.data);
  }

  getImageURI() {
    return this.page?.image?.imageUrl;
  }

  getSecondaryImageURI() {
    return this.page.secondaryImage?.imageUrl;
  }

  getComponentsForSection(section) {
    if (!this.page.components) { return []; }

    return this.page.components.filter((component) => {
      return component.layoutSection === section;
    });
  }

  getSidebarComponents() {
    return this.getComponentsForSection(SIDEBAR_SECTION).filter((c) => {
      return this.shouldRenderFirstOnMobile(c);
    });
  }

  getSidebarOverflowComponents() {
    return this.getComponentsForSection(SIDEBAR_SECTION).filter((c) => {
      return !this.shouldRenderFirstOnMobile(c);
    });
  }

  getJumplinks(): IJumplink[] {
    return this.page.components.map((c) => {
      const data = this.getData(c);
      if (!data?.jumplink) return;

      return {
        text: data.jumplink,
        id: data.jumplinkSlug,
        inSidebar: c.layoutSection === SIDEBAR_SECTION,
      };
    }).filter(Boolean);
  }

  isTwoCol() {
    return !!this.getComponentsForSection(SIDEBAR_SECTION).length;
  }

  isAdmin() {
    return get(this.props, 'user.isAdmin');
  }

  shouldRenderFirstOnMobile(c) {
    return COMPONENTS_TO_RENDER_FIRST.indexOf(c.componentType) !== -1;
  }

  useCustomJumplinkLocation() {
    if (!this.page.components) return false;
    return !!this.page.components.filter(this.filterComponentsForExperiment).find(c => c.componentType === JUMPLINK_LOCATION);
  }

  get componentOutput() {
    // we don't have LP components in commons yet
    return this.props.componentOutputOverrides || COMPONENT_OUTPUT;
  }

  renderComponents(components: CommonsCmsDynamicPage.Components[], canSsr) {
    return components.filter(this.filterComponentsForExperiment).map((component, index) => {
      if (component.componentType === JUMPLINK_LOCATION) {
        return this.renderJumplinks();
      }

      return (
        <CmsPageComponent
          key={component.id}
          component={component}
          canSsr={canSsr}
          layoutSlug={this.page.layoutSlug}
          position={index}
          cmsPageType={this.page.pageType}
          cmsPageSlug={getCmsSlug(this.props)}
        />
      );
    });
  }

  renderAttribution() {
    const author = this.page.authorName;
    if (!author) return null;

    return (
      <div className="site-wrapper size-90 align-right scaling-mtb-3">
        {I18n.t('commons.dynamicPage.footerByline', { author })}
      </div>
    );
  }

  renderSidebar() {
    const topSidebarComponents = this.getSidebarComponents();
    const bottomSidebarComponents = this.getSidebarOverflowComponents();

    return (
      <div className="dynamic-page__sidebar">
        {!!topSidebarComponents.length &&
          <div className="dynamic-page__sidebar__top">
            {this.renderComponents(topSidebarComponents, true)}
          </div>
        }
        {!!bottomSidebarComponents.length &&
          <div className="dynamic-page__sidebar__bottom">
            {this.renderComponents(bottomSidebarComponents, true)}
          </div>
        }
      </div>
    );
  }

  renderSidebarOverflow() {
    if (!this.isTwoCol()) { return null; }
    const components = this.getSidebarOverflowComponents();
    if (!components || !components.length) return null;

    return (
      <div className="dynamic-page__sidebar-mobile-overflow">
        {this.renderComponents(components, false)}
      </div>
    );
  }

  @bind
  filterComponentsForExperiment(component: CommonsCmsDynamicPage.Components) {
    return trackCmsExperimentComponentFilter({
      componentData: component,
      user: this.props.user,
    });
  }

  @bind
  addEmailPopUpTriggerIfAllowedPage() {
    if (pageShouldShowEmailPopUp(this.props.location)) {
      addOnExitTriggerForEmailPopUp(this.props.user);
    }
  }

  renderColumns() {
    const components = this.getComponentsForSection(MAIN_SECTION);

    if (this.isTwoCol()) {
      return (
        <div className="dynamic-page__two-col">
          {this.renderSidebar()}
          <div className="dynamic-page__two-col__main">
            {this.renderComponents(components, true)}
          </div>
        </div>
      );
    }

    return (
      <div className="dynamic-page__one-col">
        {this.renderComponents(components, true)}
      </div>
    );
  }

  renderNotFound() {
    if (this.props.notFoundOverride) {
      return <this.props.notFoundOverride />;
    }

    return <NotFound />;
  }

  renderJumplinks() {
    if (!this.page.components) return;
    return (
      <Jumplinks
        jumplinks={this.getJumplinks()}
        centered={!this.isTwoCol()}
      />
    );
  }

  render() {
    const { page } = this;

    // This should only be an intermediate result before the page data is loaded in RRE.
    if (this.props.data.loading && !page) { return null; }

    if (this.props.data.error) {
      throw new Error(`Error fetching CMS page data: ${this.props.data.error.message}`);
    }

    if (page.pageNotFound) {
      return this.renderNotFound();
    }

    if (!this.props.data.loading && page.pageType !== this.props.route.props?.cmsPageType) {
      return (
        <Redirect url={cmsPagePath(page)} />
      );
    }

    const classes = classNames(
      'dynamic-page',
      { 'dynamic-page--flush-with-header': page.headerStyle === 'none' },
    );

    return (
      <div className={classes}>
        {page.live || <DraftPageBanner />}
        <ScrollTarget onlyScrollUp ref={this.createScrollTargetRef} />
        <CmsPageMetadata page={page} />
        <DynamicPageHead
          actionText={page.actionText}
          actionUrl={page.actionUrl}
          authorName={page.authorName}
          goLiveAt={page.goLiveAt}
          headerStyle={page.headerStyle}
          imageUrl={this.getImageURI()}
          pageType={page.pageType}
          searchPlaceholder={page.searchPlaceholder}
          subtitle={page.description}
          title={page.title}
          secondaryImageUrl={this.getSecondaryImageURI()}
        />
        {!this.useCustomJumplinkLocation() && this.renderJumplinks()}
        <div className="dynamic-page__content">
          {this.renderColumns()}
          {this.renderSidebarOverflow()}
          {this.renderAttribution()}

          {this.isAdmin() &&
            <div className="page-edit-link">
              <a
                href={cmsEditRoute(page.id)}
              >
                {I18n.t('commons.dynamicPage.pageEditLink')}
              </a>
            </div>
          }
        </div>
      </div>
    );
  }
}

// Exported for testing
export function getCmsSlug(props: IOwnProps) {
  const { routeParams, route } = props;

  // slugOverride Route prop is used on root pages where there's no slug param in the route
  if (route.props?.slugOverride) {
    return route.props.slugOverride;
  }

  const { slug, childSlug, splat } = routeParams;

  if (splat) {
    return splat;
  }

  if (childSlug) {
    return `${slug}/${childSlug}`;
  }

  return slug;
}

const connect = graphql<IProps, CommonsCmsDynamicPage.Query>(
  gql`
    query Commons_Cms_DynamicPage(
      $slug: String
      $admin: Boolean
    )
    {
      pages: cmsPagesFetch(input: {
        slugs: [$slug],
        includeDrafts: $admin
      }) {
        _id
        id
        slug
        title
        description
        metaTitle
        metaDescription
        componentIds
        pageType
        channels
        locale
        layoutSlug
        headerStyle
        actionText
        actionUrl
        searchPlaceholder
        noIndex
        components {
          _id
          id
          position
          componentType
          data
          layoutSection
          experimentKey
          experimentBucket
        }
        image {
          imageUrl
        }
        metaImage {
          imageUrl
        }
        secondaryImage {
          imageUrl
        }
        authorName
        goLiveAt
        live
        pageNotFound
      }
    }
  `,
  {
    options: (ownProps) => {
      return {
        variables: {
          slug: getCmsSlug(ownProps),
          admin: ownProps.user?.isAdmin,
        },
      };
    },
  },
);

export default withUserContext<IOwnProps>(connect(DynamicPage));
